import { MapPin, MonitorSmartphone, Users, ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/data/content";

const HERO_IMG = "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=85&w=2000&auto=format&fit=crop";

export const Hero = () => {
  return (
    <section id="top" className="relative overflow-hidden pt-16" data-testid="hero-section">
      {/* Background */}
      <div className="absolute inset-0 z-0 bg-[#071f33]">
        <img src={HERO_IMG} alt="High school students studying together" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#071f33]/95 via-[#0B3B60]/85 to-[#0B3B60]/55" />
      </div>

      <div className="grain-overlay reveal is-visible relative z-10 mx-auto grid max-w-7xl gap-10 px-4 py-24 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:py-32">
        <div className="lg:col-span-7">
          <p className="eyebrow-label !text-amber-400" data-testid="hero-eyebrow">
            Tutoring · Witbank &amp; Online · Since {BRAND.founded}
          </p>
          <h1
            className="mt-5 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
            data-testid="hero-headline"
          >
            Real understanding.{" "}
            <span className="text-amber-400">Real marks.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg" data-testid="hero-subheadline">
            Maths, Maths Lit, Science and CAD tutoring for Grades 6–12 — in small groups with personal attention,
            from qualified, experienced tutors. Learn online from anywhere, or in person at our Witbank academy.
          </p>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-amber-500 px-7 font-heading text-base font-bold text-slate-900 shadow-lg shadow-amber-500/25 transition-transform duration-200 hover:scale-[1.03] hover:bg-amber-400"
              data-testid="hero-book-lesson-btn"
            >
              <a href="#book">
                Book a lesson <ArrowRight className="ml-1 h-4 w-4" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-white/40 bg-white/10 px-7 font-heading text-base font-bold text-white backdrop-blur transition-transform duration-200 hover:scale-[1.03] hover:bg-white/20 hover:text-white"
              data-testid="hero-consultation-btn"
            >
              <a href="#consultation">
                <MessageCircle className="mr-1 h-4 w-4" /> Free consultation
              </a>
            </Button>
          </div>

          <div className="mt-12 grid max-w-xl grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              { icon: Users, label: "Small groups, personal attention" },
              { icon: MapPin, label: "In person in Witbank" },
              { icon: MonitorSmartphone, label: "Online anywhere in SA" },
            ].map(({ icon: Icon, label }, i) => (
              <div
                key={label}
                className="flex items-center gap-2.5 rounded-xl border border-white/15 bg-white/10 px-4 py-3 backdrop-blur"
                data-testid={`hero-badge-${i}`}
              >
                <Icon className="h-5 w-5 shrink-0 text-amber-400" />
                <span className="text-xs font-semibold leading-snug text-white">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Floating card */}
        <div className="hidden items-end lg:col-span-5 lg:flex">
          <div className="ml-auto w-full max-w-sm rounded-2xl border border-white/15 bg-white/95 p-8 shadow-2xl backdrop-blur" data-testid="hero-subject-card">
            <p className="eyebrow-label">Subjects</p>
            <ul className="mt-4 space-y-3.5">
              {[
                ["Mathematics Core", "Gr 6–12"],
                ["Mathematical Literacy", "Gr 6–12"],
                ["Science", "Gr 9–12"],
                ["CAD — Draughting", "Gr 9–12"],
              ].map(([name, grade]) => (
                <li key={name} className="flex items-center justify-between border-b border-border pb-3 last:border-0 last:pb-0">
                  <span className="font-heading text-sm font-bold text-slate-900">{name}</span>
                  <span className="rounded-full bg-secondary/10 px-2.5 py-0.5 text-xs font-bold text-secondary">{grade}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
              Online &amp; in-person options for every subject. Part of {BRAND.fullName}, established {BRAND.founded}.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
